/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { alias } from '@ember/object/computed';
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import EnrolmentStatus from 'linced-app/enums/enrolment-status';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import ENV from 'linced-app/config/environment';

export default class ConflictMerge extends Component {
  @service
  fetch;

  @service
  session;

  @service
  router;

  @service
  permissions;

  @service
  roles;

  @tracked entity = null;
  @tracked
  entityFieldValue = null;
  @tracked
  mergeComplete = false;
  @tracked
  mergedPersonId = null;
  @tracked
  mergeFailed = false;
  @tracked merging = false;
  originalValue = null;
  @tracked
  searchResponse = null;
  redirect = true;

  @alias('searchResponse.searchResult')
  searchResult;

  @tracked
  detailDialogOpen;

  // Check if our search result has a current enrolment at any school
  // and our entity has a current enrolment at this school (this is bad)
  @computed(
    'searchResponse.searchResult.hasCurrentEnrolment',
    'entity.enrolmentStatus',
  )
  get currentEnrolmentConflict() {
    return (
      this.searchResponse.searchResult.hasCurrentEnrolment &&
      this.entity.enrolmentStatus === EnrolmentStatus.CURRENT
    );
  }

  // Check if our entities have both a current enrolment and pre-enrolment at this school
  // We can't merge if the merged student would have both of these enrolments
  @computed(
    'searchResponse.searchResult.inThisSchool',
    'entity.enrolmentStatus',
  )
  get currentPreEnrolConflict() {
    // inThisSchool returns true if the searchResult is currently enrolled, pending, or pre-enrolled in this school
    return (
      this.searchResponse.searchResult.inThisSchool &&
      [
        EnrolmentStatus.CURRENT,
        EnrolmentStatus.PRE_ENROLLED,
        EnrolmentStatus.PENDING,
      ].includes(this.entity.enrolmentStatus)
    );
  }

  @computed('searchResponse.searchResult')
  get conflictDialogOpen() {
    return !!this.searchResponse && !!this.searchResponse.searchResult;
  }

  @computed('merging', 'currentEnrolmentConflict', 'currentPreEnrolConflict')
  get disableMerge() {
    return (
      this.merging ||
      this.currentEnrolmentConflict ||
      this.currentPreEnrolConflict
    );
  }

  @computed('session.scopes.[]')
  get isTenantAdmin() {
    return this.permissions.meet(Scopes.TENANT_ADMIN);
  }

  @computed('entity', 'searchResult')
  get studentsMatch() {
    // Confirm we have a searchresponse searchResult so things don't explode
    if (isEmpty(this.searchResult)) {
      return false;
    }
    // Check DOB first
    if (this.entity.dob !== this.searchResult.dob) {
      return false;
    }
    let entityFirstName = this.entity.legalFirstName.toLowerCase().trim();
    let entityLastName = this.entity.legalLastName.toLowerCase().trim();
    let searchResultFirstName = this.searchResult.legalFirstName
      .toLowerCase()
      .trim();
    let searchResultLastName = this.searchResult.legalLastName
      .toLowerCase()
      .trim();
    // Check current entity first name existing at the start of searchResult first name or vice versa
    // This handles the case where the middle name is included in one or the other
    if (
      entityFirstName.indexOf(searchResultFirstName) !== 0 &&
      searchResultFirstName.indexOf(entityFirstName) !== 0
    ) {
      return false;
    }
    // Just check for a straight match on last name
    if (entityLastName !== searchResultLastName) {
      return false;
    }

    return true;
  }

  @computed('isTenantAdmin', 'studentsMatch', 'roles.hasLearnerMergeAccess')
  get canMerge() {
    return (
      (this.isTenantAdmin && this.studentsMatch) ||
      this.roles.hasLearnerMergeAccess
    );
  }

  @action
  closeConflictDialog() {
    this.entityFieldValue = this.originalValue;
    this.set('entity.jurisdictionalId', this.originalValue);
    this.searchResponse = null;
    this.mergeFailed = false;
  }

  @action
  closeConflictDialogAfterMerge() {
    // Close the Check NSN dialog if necessary
    if (!isEmpty(this.detailDialogOpen)) {
      this.detailDialogOpen = false;
    }
    if (!isEmpty(this.mergedPersonId)) {
      if (this.redirect) {
        // Redirect if we did a reverse merge and our current person id no longer exists
        // Default selectedTab to zero as leaving it empty on transition seems to fail to load the default tab
        let selectedTab =
          this.router.get('currentRoute.queryParams.selectedTab') ?? 0;
        this.router.replaceWith(
          'people.person.information',
          this.mergedPersonId,
          { queryParams: { selectedTab } },
        );
      } else {
        window.location.reload();
      }
    }
    this.searchResponse = null;
    this.mergeComplete = false;
  }

  @action
  mergeRecords() {
    this.merging = true;
    let data = {
      personId: this.get('entity.id'),
      duplicatePersonId: this.get('searchResponse.searchResult.personId'),
      mergedPerson: this.entity.serialize(),
    };
    let options = {
      data: JSON.stringify(data),
    };
    this.fetch
      .post(
        `${ENV.APP.apiHost}/people/v4/people/${this.get('entity.id')}/:merge`,
        options,
      )
      .then(
        (mergedPerson) => {
          // Revert values to avoid the "unsaved changes" warning or superfluous save button
          this.entity.rollbackAttributes();
          if (mergedPerson.person.id !== this.get('entity.id')) {
            this.mergedPersonId = mergedPerson.person.id;
            this.mergeComplete = true;
            this.merging = false;
          } else {
            // Do a quick reload and save to ensure the backend lines up the jurisdictionalId and field
            this.entity.reload().then((entity) => {
              entity.save().then(() => {
                this.mergeComplete = true;
                this.merging = false;
              });
            });
          }
        },
        () => {
          this.mergeFailed = true;
          this.merging = false;
        },
      );
  }
}
